import React from 'react'
import classnames from 'classnames/bind'
import { fromUnixTime } from 'date-fns'
import pluralize from '@caudena/core/pluralize'

import { Typography } from '@caudena/core/ui-kit'
import { InfoCard, InfoCardStub } from '@caudena/core/ui-kit'
import { Score } from '@caudena/core/ui-kit'
import Link from '@caudena/core/Link'
import { RowDeprecated } from '@caudena/core/ui-kit'
import { TagDeprecated } from '@caudena/core/ui-kit'
import { MagicGrid } from '@caudena/core/ui-kit'
import { CopyIcon } from '@caudena/core/ui-kit'
import { Container } from '@caudena/core/ui-kit'
import { tokenIdToCurrency } from '@caudena/core/utils'
import { formatMoney, formatNumber } from '@caudena/core/utils/format'
import { useFormatDate, useTimezone } from '../../hooks'
import { ActivityPeriod } from '@caudena/core/ui-kit'
import { observer } from 'mobx-react-lite'
import { Counterparty } from '../../types/Counterparty'

import styles from './index.scss'
import { CurrencyValue } from '../../types/coin'
import { SnakeToCamelCaseObject } from '@caudena/core/utilsTypes'
import { getCurrency } from '../../constants/currency'

const cx = classnames.bind(styles)

interface CounterpartyInfoCardProps {
  className?: string
  counterparty?: SnakeToCamelCaseObject<Counterparty['data']>
  currency?: CurrencyValue
  loading?: boolean
}

const CounterpartyInfoCard: React.FC<CounterpartyInfoCardProps> = ({
  loading,
  counterparty,
  className,
  currency,
}) => {
  const timezone = useTimezone()
  const formatDate = useFormatDate()

  if (loading) {
    return (
      <Container gap={1.25} className={cx('CounterpartyInnerCard')}>
        <InfoCardStub rows={7} />
      </Container>
    )
  }

  if (!counterparty) return null

  const activityPeriod = [
    fromUnixTime(counterparty.firstSeen),
    fromUnixTime(counterparty.lastSeen),
  ] as [Date, Date]
  const addressCount = counterparty?.size ?? 0
  const coin =
    typeof currency === 'number'
      ? tokenIdToCurrency[currency] ?? getCurrency(currency)
      : currency

  return (
    <div className={cx('CounterpartyInfoCard', className)}>
      <Container gap={1.25} className={cx('CounterpartyInnerCard')}>
        <MagicGrid gap={2}>
          <div className={cx('CounterpartyInfoCardHeader')}>
            <RowDeprecated>
              <Link
                to="%PLATFORM_URL%/:coin/cluster/:id"
                params={{ coin, id: counterparty.clusterId }}
              >
                <Typography variant="heading3">
                  {counterparty.entity?.name || counterparty.clusterId}
                </Typography>
              </Link>
              <RowDeprecated gap={0.25}>
                <Score
                  className={cx('CounterpartyInfoCardScore')}
                  value={counterparty.score}
                />
                {counterparty.entity?.category && (
                  <TagDeprecated>{counterparty.entity?.category}</TagDeprecated>
                )}
              </RowDeprecated>
            </RowDeprecated>
          </div>
          <MagicGrid>
            <ActivityPeriod
              timeZone={timezone}
              today={formatDate(new Date(), 'date')}
              activityPeriod={activityPeriod}
              currency={coin}
            />
            <InfoCard
              details={
                counterparty?.size
                  ? [
                      {
                        label: 'Size',
                        value: (
                          <Typography variant="body1" color="grey1">
                            {formatNumber(addressCount, 0)}{' '}
                            {pluralize('address', addressCount)}
                          </Typography>
                        ),
                      },
                    ]
                  : []
              }
            />
          </MagicGrid>
        </MagicGrid>
      </Container>
      <Container gap={1.25} className={cx('CounterpartyInnerCard', 'right')}>
        <InfoCard
          details={[
            {
              label: (
                <>
                  Transfers
                  <Typography variant="caption1">
                    (
                    {formatNumber(
                      (counterparty.trxCount.in ?? 0) +
                        (counterparty.trxCount.out ?? 0),
                      0
                    )}
                    )
                  </Typography>
                </>
              ),
              value: (
                <RowDeprecated gap={0.5}>
                  <Typography>
                    {formatMoney({
                      value:
                        (counterparty.balanceUsd.totalIn ?? 0) +
                        (counterparty.balanceUsd.totalOut ?? 0),
                      currency: 'usd',
                      precision: 0,
                      code: '',
                    })}
                  </Typography>
                  <CopyIcon
                    value={
                      (counterparty.balanceUsd.totalIn ?? 0) +
                      (counterparty.balanceUsd.totalOut ?? 0)
                    }
                  />
                </RowDeprecated>
              ),
            },
            {
              label: 'Total received',
              value: (
                <RowDeprecated gap={0.5}>
                  <Typography>
                    {formatMoney({
                      value: counterparty.balanceUsd.totalIn ?? 0,
                      currency: 'usd',
                      precision: 0,
                      code: '',
                    })}
                  </Typography>
                  <CopyIcon value={counterparty.balanceUsd.totalIn ?? 0} />
                </RowDeprecated>
              ),
            },
            {
              label: 'Total sent',
              value: (
                <RowDeprecated gap={0.5}>
                  <Typography>
                    {formatMoney({
                      value: counterparty.balanceUsd.totalOut ?? 0,
                      currency: 'usd',
                      precision: 0,
                      code: '',
                    })}
                  </Typography>
                  <CopyIcon value={counterparty.balanceUsd.totalOut ?? 0} />
                </RowDeprecated>
              ),
            },
            {
              label: 'Balance',
              value: (
                <RowDeprecated gap={0.5}>
                  <Typography>
                    {formatMoney({
                      value: counterparty.balanceUsd.balance ?? 0,
                      currency: 'usd',
                      precision: 0,
                      code: '',
                    })}
                  </Typography>
                  <CopyIcon value={counterparty.balanceUsd.balance ?? 0} />
                </RowDeprecated>
              ),
            },
          ]}
        />
      </Container>
    </div>
  )
}

export default observer(CounterpartyInfoCard)
